<script setup>
import { ref, reactive, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import FormCheckbox from '@/components/Atoms/FormCheckbox.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconPlayerFullScreen from '@/components/icon/IconPlayerFullScreen.vue'
import IconOpenExternal from '@/components/icon/IconOpenExternal.vue'
import IconPlayerVolumeOn from '@/components/icon/IconPlayerVolumeOn.vue'
import IconPlayerVolumeOff from '@/components/icon/IconPlayerVolumeOff.vue'
import IconLightning from '@/components/icon/IconLightning.vue'
import IconGrid from '@/components/icon/IconGrid.vue'
import IconPosition from '@/components/icon/IconPosition.vue'
import IconMultiviewLayout from '@/components/icon/IconMultiviewLayout.vue'
import IconChevronDown from '@/components/icon/IconChevronDown.vue'
import IconElipsis from '@/components/icon/IconElipsis.vue'
import IconUndo from '@/components/icon/IconUndo.vue'
import IconRedo from '@/components/icon/IconRedo.vue'
import MediaflowSceneCanvas from './MediaflowSceneCanvas.vue'
import MediaflowSendToLiveButton from './MediaflowSendToLiveButton.vue'
import MediaflowMonitorHeader from './MediaflowMonitorHeader.vue'
import { layoutTemplates, heavyLayerCount, layerBox, boxToTransform, clampToCanvas, clampTransform, containRect } from './sceneGeometry'
import { draftApplyLabel } from './mediaflowMeta'

/**
 * The Preview monitor: "● PREVIEW · ‹scene›" header, the interactive browser
 * canvas, a thin toolbar strip under it (layout templates, snap, fine-tune,
 * lightweight, audio, Edit on air, fullscreen, pop-out), then the Fine-tune
 * numbers. A layout draft takes over the strip itself (same height), so the
 * docks below never move when one appears.
 */
const props = defineProps({
	/** useMediaflowDesk() instance. */
	desk: { type: Object, required: true },
	canvas: { type: Object, required: true },
	streams: { type: Array, default: () => [] },
	sessionActive: { type: Boolean, default: false },
	/** Production disabled → nothing is editable. */
	enabled: { type: Boolean, default: true },
	/** Mobile: canvas is view-only. */
	interactive: { type: Boolean, default: true },
	/** Hide the pop-out button inside the pop-out window itself. */
	popout: { type: Boolean, default: false },
	/** Why Send to live is disabled; empty when usable. */
	sendDisabledReason: { type: String, default: '' },
	loading: { type: Boolean, default: false },
	/** Show the Send to live button in the header (the control room keeps it on the transition rail). */
	showSend: { type: Boolean, default: false },
	/** Inputs can be dropped on the canvas (emits `drop-input`). */
	dropTarget: { type: Boolean, default: false },
	portrait: { type: Boolean, default: false },
})

const emit = defineEmits(['popout', 'send', 'drop-input'])

const canvasRef = ref(null)
const audioMonitor = ref(false)
const lightweight = ref(false)
const snap = ref(true)
const dismissedHeavyHint = ref(false)
const templatesMenu = ref(null)

// The scene with its draft applied (staged adds / removes / order / visibility / settings).
const flow = computed(() => props.desk.previewFlow?.value ?? props.desk.editingFlow.value)
const onAir = computed(() => props.desk.editingIsOnAir.value)
const liveEdit = computed(() => props.desk.liveEdit.value)
const applyLabel = computed(() => draftApplyLabel(props.sessionActive))
const pendingCount = computed(() => props.desk.pendingCount.value)
const selectedId = computed(() => props.desk.selectedMediaId.value)
const editingOverlay = computed(() => props.desk.editingOverlay?.value || null)
const selectedMedia = computed(() => (
	(flow.value?.media || []).find((m) => m.id === selectedId.value)
	|| (editingOverlay.value?.media?.id === selectedId.value ? editingOverlay.value.media : null)
))
const editable = computed(() => props.enabled && (!!flow.value || !!editingOverlay.value))
const showSendButton = computed(() => props.showSend && !!flow.value && !onAir.value)
/**
 * Graphics that will be on air after Apply draw on the preview (staged takes
 * included); the edited one too, ghosted when off air.
 */
const canvasOverlays = computed(() => {
	const all = props.desk.previewOverlays?.value || props.desk.overlays?.value || []
	return all.filter((o) => o.live || o._id === editingOverlay.value?._id)
})
// "Edit on air" matters for the on-air flow and for a live graphic alike.
const liveTarget = computed(() => onAir.value || !!editingOverlay.value?.live)

const heavy = computed(() => heavyLayerCount(flow.value?.media))
const suggestLightweight = computed(() => heavy.value > 4 && !lightweight.value && !dismissedHeavyHint.value)
watch(() => flow.value?._id, () => { dismissedHeavyHint.value = false })

const templates = computed(() => layoutTemplates(props.canvas))
const applyTemplate = (template) => {
	if (!selectedMedia.value) return
	props.desk.updateTransform(selectedMedia.value, clampTransform(template.transform, props.canvas), { final: true })
	templatesMenu.value?.hide?.()
}

const onTransform = (media, transform, meta) => props.desk.updateTransform(media, transform, meta)
const onSelect = (id) => props.desk.selectMedia(id)

// ---------------------------------------------------------------- Position popover
// Numbers are the visible content rect (what the selection hugs), live while dragging.
const positionOpen = ref(false)
const gestureBox = ref(null)
const onGestureBox = (value) => { gestureBox.value = value }
const geometry = computed(() => (selectedId.value ? canvasRef.value?.layerGeometry(selectedId.value) || null : null))
const liveRect = computed(() => {
	if (gestureBox.value && gestureBox.value.mediaId === selectedId.value) return gestureBox.value.box
	if (geometry.value) return geometry.value.rect
	return selectedMedia.value ? layerBox(props.desk.effectiveTransform(selectedMedia.value), props.canvas) : null
})
const contentAspectOf = computed(() => geometry.value?.aspect || null)
const fields = reactive({ x: '', y: '', w: '', h: '' })
const fieldsDirty = ref(false)
const syncFields = () => {
	const r = liveRect.value
	if (!r) return
	fields.x = String(Math.round(r.x)); fields.y = String(Math.round(r.y))
	fields.w = String(Math.round(r.w)); fields.h = String(Math.round(r.h))
}
watch(liveRect, () => { if (!fieldsDirty.value) syncFields() }, { immediate: true, deep: true })
const num = (v, fallback) => { const n = parseFloat(v); return Number.isFinite(n) ? n : fallback }
// Typing moves the picture at once (no save); the edit is committed on Enter,
// blur, a pause, closing the bar or picking another source, to the source it
// was typed for.
const FIELD_COMMIT_MS = 400
let edit = null // { media, base } while fields are dirty
let fieldTimer = 0
const fieldsRect = () => {
	const b = edit.base
	return clampToCanvas({ x: num(fields.x, b.x), y: num(fields.y, b.y), w: num(fields.w, b.w), h: num(fields.h, b.h) }, props.canvas)
}
const pushFields = (final) => {
	if (!edit) return
	props.desk.updateTransform(edit.media, boxToTransform(fieldsRect(), props.canvas), { final })
}
// Content keeps its shape: width and height move together when it has one.
const onField = (key, value) => {
	if (!selectedMedia.value || !liveRect.value) return
	if (!edit) edit = { media: selectedMedia.value, base: { ...liveRect.value } }
	fields[key] = value
	fieldsDirty.value = true
	const a = contentAspectOf.value
	const n = parseFloat(value)
	if (a && Number.isFinite(n) && n > 0) {
		if (key === 'w') fields.h = String(Math.round(n / a))
		if (key === 'h') fields.w = String(Math.round(n * a))
	}
	pushFields(false)
	clearTimeout(fieldTimer)
	fieldTimer = setTimeout(() => applyFields(), FIELD_COMMIT_MS)
}
const applyFields = ({ sync = true } = {}) => {
	clearTimeout(fieldTimer)
	if (!edit || !fieldsDirty.value) return
	pushFields(true)
	edit = null
	fieldsDirty.value = false
	// A clamped result can differ from what was typed: show what actually landed.
	if (sync) nextTick(syncFields)
}
const commitRect = (rect, { clamp = true } = {}) => {
	if (!selectedMedia.value) return
	applyFields({ sync: false })
	const next = clamp ? clampToCanvas(rect, props.canvas) : rect
	props.desk.updateTransform(selectedMedia.value, boxToTransform(next, props.canvas), { final: true })
}
const onFieldsFocusOut = (event) => {
	if (event.currentTarget.contains(event.relatedTarget)) return
	applyFields()
}
// ↑/↓ inside a field: ±1, Shift ±10. Enter commits.
const onFieldKeydown = (event) => {
	const key = event.target?.dataset?.key
	if (!key) return
	if (event.key === 'Enter') { event.preventDefault(); applyFields(); return }
	if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
	event.preventDefault()
	const base = num(fields[key], liveRect.value?.[key] ?? 0)
	onField(key, String(Math.round(base + (event.key === 'ArrowUp' ? 1 : -1) * (event.shiftKey ? 10 : 1))))
}
const canvasRectFull = computed(() => ({ x: 0, y: 0, w: props.canvas.width, h: props.canvas.height }))
const fitRect = computed(() => containRect(canvasRectFull.value, contentAspectOf.value))
const fillRect = computed(() => {
	const a = contentAspectOf.value
	const { width: W, height: H } = props.canvas
	if (!a) return canvasRectFull.value
	if (a > W / H) { const w = H * a; return { x: (W - w) / 2, y: 0, w, h: H } }
	const h = W / a
	return { x: 0, y: (H - h) / 2, w: W, h }
})
const near = (a, b) => !!a && !!b && ['x', 'y', 'w', 'h'].every((k) => Math.abs(a[k] - b[k]) < 2)
const fitMode = computed(() => (near(liveRect.value, fillRect.value) && !near(fitRect.value, fillRect.value) ? 'fill' : near(liveRect.value, fitRect.value) ? 'fit' : ''))
// Fill may overflow the canvas on purpose (the renderer crops it).
const setFit = (mode) => commitRect(mode === 'fill' ? fillRect.value : fitRect.value, { clamp: mode !== 'fill' })
const resetFullscreen = () => {
	if (!selectedMedia.value) return
	applyFields({ sync: false })
	props.desk.updateTransform(selectedMedia.value, { positionX: 0, positionY: 0, scaleX: 100, scaleY: 100 }, { final: true })
}
const togglePosition = () => { positionOpen.value = !positionOpen.value }
// The bar follows the selection: it closes when nothing is selected or
// another scene is previewed, and never drops a half-typed edit.
watch(selectedId, (id) => {
	applyFields({ sync: false })
	syncFields()
	if (!id) positionOpen.value = false
})
watch(() => flow.value?._id, () => { applyFields({ sync: false }); positionOpen.value = false })
watch(positionOpen, (open) => { if (!open) applyFields({ sync: false }) })

// ---------------------------------------------------------------- Edit on air
// A small confirm by the switch; "don't ask again" lasts for this browser session.
const NO_ASK_KEY = 'castr-mf-live-edit-noask'
const liveAskOpen = ref(false)
const liveAskNoAgain = ref(false)
const liveAskEl = ref(null)
const noAskStored = () => { try { return sessionStorage.getItem(NO_ASK_KEY) === '1' } catch (e) { return false } }
const enableLiveEdit = async () => {
	// What the canvas shows is what goes live: an open draft is applied first.
	if (pendingCount.value > 0 && !(await props.desk.applyDraft())) return
	props.desk.setLiveEdit(true)
}
// The switch only turns on after the confirm: re-keying it puts the clicked
// checkbox back to off while the confirm is open (or after Cancel).
const liveSwitchKey = ref(0)
const onLiveEditToggle = (value) => {
	if (!value) { liveAskOpen.value = false; props.desk.setLiveEdit(false); return }
	if (liveAskOpen.value) { liveAskOpen.value = false; liveSwitchKey.value += 1; return }
	if (noAskStored() && pendingCount.value === 0) { void enableLiveEdit(); return }
	liveAskNoAgain.value = false
	liveAskOpen.value = true
	liveSwitchKey.value += 1
}
const cancelLiveEdit = () => { liveAskOpen.value = false }
const confirmLiveEdit = async () => {
	if (liveAskNoAgain.value) { try { sessionStorage.setItem(NO_ASK_KEY, '1') } catch (e) { /* private mode */ } }
	await enableLiveEdit()
	liveAskOpen.value = false
}

// Escape closes the top-most popover first (and nothing else); a click
// outside the Edit on air confirm cancels it.
const onDocKeydown = (event) => {
	if (event.key !== 'Escape') return
	if (liveAskOpen.value) { liveAskOpen.value = false } else if (positionOpen.value) { positionOpen.value = false } else return
	event.stopPropagation()
	event.preventDefault()
}
const onDocPointerDown = (event) => {
	if (!liveAskOpen.value) return
	const t = event.target
	if (liveAskEl.value?.contains(t) || t?.closest?.('[data-testid="live-edit-toggle"], [data-testid="strip-more-live-edit"]')) return
	liveAskOpen.value = false
}
watch(() => liveAskOpen.value || positionOpen.value, (open) => {
	if (open) {
		document.addEventListener('keydown', onDocKeydown, true)
		document.addEventListener('pointerdown', onDocPointerDown, true)
	} else {
		document.removeEventListener('keydown', onDocKeydown, true)
		document.removeEventListener('pointerdown', onDocPointerDown, true)
	}
})
onBeforeUnmount(() => {
	document.removeEventListener('keydown', onDocKeydown, true)
	document.removeEventListener('pointerdown', onDocPointerDown, true)
})
watch(() => flow.value?._id, () => { liveAskOpen.value = false })

const drafting = computed(() => pendingCount.value > 0)
const draftLabel = computed(() => `${pendingCount.value} unsaved ${pendingCount.value === 1 ? 'change' : 'changes'}`)
const draftShortLabel = computed(() => `${pendingCount.value} unsaved`)
const moreMenu = ref(null)
const closeMore = () => moreMenu.value?.hide?.()
const toggleFullscreen = () => canvasRef.value?.toggleFullscreen()
const toolButton = (active) => (active ? '!text-white bg-surface-3' : '')

defineExpose({ toggleFullscreen })
</script>

<template>
	<!--
		On lg+ the root is `display: contents`: header, video, toolbar and extras
		drop into the parent's monitor grid (rows 1–4, column 1) so they line up
		row for row with the Program monitor and the transition rail.
	-->
	<div class="flex min-w-0 flex-col gap-2 lg:contents" aria-label="Preview monitor" data-testid="editor-panel" data-monitor="preview">
		<!-- Header: one green tally -->
		<MediaflowMonitorHeader class="lg:col-start-1 lg:row-start-1" label="Preview" tone="preview" testid="preview" :name="flow?.name || ''" placeholder="No scene selected">
			<template #badges>
				<Tooltip v-if="onAir" :content="sessionActive ? 'On Program. Edits stay a draft until you apply them.' : 'The stream starts with this scene. Edits stay a draft until you apply them.'" wrapper-class="inline-flex shrink-0" popper-class="max-w-[240px] text-sm break-words">
					<span
						class="inline-flex h-[18px] items-center rounded border px-1.5 text-[0.6rem] font-semibold leading-none tracking-wider"
						:class="sessionActive ? 'border-error text-error-light' : 'border-surface-6 text-surface-text-2'"
						:data-live="sessionActive ? 'true' : 'false'"
						data-testid="editor-on-air"
					>{{ sessionActive ? 'ON AIR' : 'CUED' }}</span>
				</Tooltip>
				<span v-else-if="flow?.kind === 'break'" class="inline-flex h-[18px] shrink-0 items-center rounded border border-surface-5 px-1.5 text-[0.6rem] font-semibold uppercase leading-none tracking-wider text-surface-8">Break</span>
			</template>
			<template #actions>
				<span v-if="editingOverlay" class="hidden min-w-0 shrink truncate text-xs text-surface-8 xl:inline" data-testid="editing-overlay">Positioning <span class="text-surface-text-1">{{ editingOverlay.name }}</span></span>
				<MediaflowSendToLiveButton
					v-if="showSendButton"
					class="hidden md:inline-flex"
					:desk="desk"
					:session-active="sessionActive"
					:disabled-reason="sendDisabledReason"
					size="sm"
					@send="emit('send')"
				/>
			</template>
		</MediaflowMonitorHeader>

		<!-- Video -->
		<div class="relative min-w-0 lg:col-start-1 lg:row-start-2" :class="portrait ? 'mx-auto w-full max-w-[min(100%,calc(60vh*9/16))]' : ''">
			<MediaflowSceneCanvas
				ref="canvasRef"
				:flow="flow"
				:canvas="canvas"
				:transform-for="desk.effectiveTransform"
				:selected-id="selectedId"
				:editable="editable"
				:interactive="interactive"
				:on-air="onAir && sessionActive"
				:live-editing="liveTarget && liveEdit"
				:audio-monitor="audioMonitor"
				:lightweight="lightweight"
				:snap="snap"
				:streams="streams"
				:loading="loading"
				:overlays="canvasOverlays"
				:drop-target="dropTarget && editable"
				@select="onSelect"
				@transform="onTransform"
				@gesture-box="onGestureBox"
				@drop-input="(payload, point) => emit('drop-input', payload, point)"
			/>

			<!-- Edit on air confirm, by the switch -->
			<div
				v-if="liveAskOpen"
				ref="liveAskEl"
				class="mf-popover absolute bottom-1.5 z-[1100] w-[280px] rounded-lg bg-surface-2 p-3 ring-1 ring-surface-4"
				:class="drafting ? 'left-1.5' : 'right-1.5'"
				role="dialog"
				aria-labelledby="mf-live-ask-title"
				data-testid="live-edit-popover"
			>
				<p id="mf-live-ask-title" class="m-0 text-sm font-semibold text-white">Edit on air?</p>
				<p class="m-0 mt-1 text-xs text-surface-8">Changes reach viewers when you drop them.</p>
				<p v-if="pendingCount > 0" class="m-0 mt-1 text-xs text-gold">Your {{ pendingCount }} unsaved {{ pendingCount === 1 ? 'change goes' : 'changes go' }} live too.</p>
				<FormCheckbox v-model="liveAskNoAgain" class="mt-2.5" name="mf-live-edit-noask" :input-value="true" label="Don’t ask again this session" data-testid="live-edit-noask" />
				<div class="mt-3 flex justify-end gap-2">
					<FormButton type="secondary" size="sm" data-testid="live-edit-cancel" @click="cancelLiveEdit">Cancel</FormButton>
					<FormButton type="red" size="sm" :is-loading="desk.applying.value" data-testid="live-edit-confirm" @click="confirmLiveEdit">Edit on air</FormButton>
				</div>
			</div>
		</div>

		<!-- Toolbar strip under the video (never shifts it); the Position bar hangs below it -->
		<div v-if="interactive" class="mf-strip-cell relative min-w-0 lg:col-start-1 lg:row-start-3">
		<div
			class="mf-strip flex h-9 min-w-0 items-center gap-0.5 overflow-hidden rounded-lg bg-surface-2 px-1 ring-1 ring-surface-3"
			:class="{ 'mf-strip--draft': drafting, 'mf-strip--live': !drafting && liveTarget && liveEdit }"
			role="toolbar"
			aria-label="Canvas tools"
			:data-draft="drafting ? 'true' : 'false'"
			data-testid="preview-strip"
		>
			<Tooltip :content="!editable ? 'Enable the production to edit layouts' : selectedMedia ? `Place ${selectedMedia.name} with a preset` : 'Select a source first'" wrapper-class="inline-flex">
				<DropDownMenu
					ref="templatesMenu"
					type="custom"
					size="sm"
					position="left"
					use-teleport
					max-height="none"
					:disabled="!editable || !selectedMedia"
					aria-label="Layout templates"
					list-classes="min-w-[200px]"
					:custom-classes="`h-7 inline-flex items-center gap-1.5 rounded-md px-2 text-xs font-medium ring-1 ${!editable || !selectedMedia ? 'ring-surface-3 text-surface-6 opacity-60 cursor-not-allowed pointer-events-none' : 'bg-surface-1 ring-surface-4 text-surface-text-1 hover:ring-surface-6 hover:text-white'}`"
					data-testid="layout-templates"
				>
					<template #toggle-button>
						<icon-base width="14" height="14" fill="none"><icon-multiview-layout /></icon-base>
						<template v-if="!drafting">
							<span>Layout</span>
							<icon-base width="10" height="10" fill="none"><icon-chevron-down /></icon-base>
						</template>
					</template>
					<template #menu-items>
						<li class="px-3 pb-1 pt-2 text-[0.65rem] font-semibold uppercase tracking-wider text-surface-6">{{ selectedMedia?.name }}</li>
						<DropDownMenuItem v-for="t in templates" :key="t.key" @click="applyTemplate(t)">
							<span class="inline-flex items-center gap-2">
								<span class="relative inline-block h-3.5 w-6 rounded-[2px] bg-surface-4" aria-hidden="true">
									<span class="absolute rounded-[1px] bg-main-light" :style="{ left: `${(t.transform.positionX / canvas.width) * 100}%`, top: `${(t.transform.positionY / canvas.height) * 100}%`, width: `${t.transform.scaleX}%`, height: `${t.transform.scaleY}%` }" />
								</span>
								{{ t.label }}
							</span>
						</DropDownMenuItem>
					</template>
				</DropDownMenu>
			</Tooltip>
			<!-- Draft: the tools fold into one menu so the draft actions fit the same row. -->
			<DropDownMenu
				v-if="drafting"
				ref="moreMenu"
				type="custom"
				size="sm"
				position="left"
				use-teleport
				max-height="none"
				aria-label="More canvas tools"
				list-classes="min-w-[210px]"
				custom-classes="h-7 w-7 inline-flex shrink-0 items-center justify-center rounded-md text-surface-8 hover:bg-surface-3 hover:text-white"
				data-testid="strip-more"
			>
				<template #toggle-button>
					<icon-base width="16" height="16"><icon-elipsis /></icon-base>
				</template>
				<template #menu-items>
					<DropDownMenuItem data-testid="strip-more-snap" @click="snap = !snap; closeMore()">
						<span class="flex w-full items-center justify-between gap-3">Snap to edges<span class="text-xs text-surface-7">{{ snap ? 'On' : 'Off' }}</span></span>
					</DropDownMenuItem>
					<DropDownMenuItem :disabled="!editable || !selectedMedia" data-testid="strip-more-position" @click="togglePosition(); closeMore()">
						<span class="flex w-full items-center justify-between gap-3">Position…<span class="text-xs text-surface-7">{{ positionOpen ? 'Open' : '' }}</span></span>
					</DropDownMenuItem>
					<DropDownMenuItem @click="lightweight = !lightweight; closeMore()">
						<span class="flex w-full items-center justify-between gap-3">Lightweight<span class="text-xs text-surface-7">{{ lightweight ? 'On' : 'Off' }}</span></span>
					</DropDownMenuItem>
					<DropDownMenuItem @click="audioMonitor = !audioMonitor; closeMore()">
						<span class="flex w-full items-center justify-between gap-3">Monitor audio<span class="text-xs text-surface-7">{{ audioMonitor ? 'On' : 'Off' }}</span></span>
					</DropDownMenuItem>
					<DropDownMenuItem v-if="liveTarget && enabled" data-testid="strip-more-live-edit" @click="closeMore(); onLiveEditToggle(!liveEdit)">
						<span class="flex w-full items-center justify-between gap-3">Edit on air<span class="text-xs text-surface-7">{{ liveEdit ? 'On' : 'Off' }}</span></span>
					</DropDownMenuItem>
					<DropDownMenuItem @click="closeMore(); toggleFullscreen()">Fullscreen</DropDownMenuItem>
					<DropDownMenuItem v-if="!popout" @click="closeMore(); emit('popout')">Pop out</DropDownMenuItem>
				</template>
			</DropDownMenu>
			<template v-else>
			<span class="w-1.5 shrink-0" aria-hidden="true" />
			<Tooltip :content="snap ? 'Snapping on' : 'Snapping off'" wrapper-class="inline-flex">
				<FormButton type="light" is-icon size="sm" :class="toolButton(snap)" :aria-pressed="snap" aria-label="Snap to edges and layers" data-testid="snap-toggle" @click="snap = !snap">
					<icon-base width="14" height="14"><icon-grid /></icon-base>
				</FormButton>
			</Tooltip>
			<Tooltip :content="selectedMedia ? 'Position and size (numbers)' : 'Select a source first'" wrapper-class="inline-flex">
				<FormButton type="light" is-icon size="sm" :class="toolButton(positionOpen)" :aria-pressed="positionOpen" :disabled="!editable || !selectedMedia" aria-label="Position" data-testid="position-toggle" @click="togglePosition">
					<icon-base width="15" height="15" fill="none"><icon-position /></icon-base>
				</FormButton>
			</Tooltip>
			<Tooltip :content="lightweight ? 'Lightweight on: stills instead of video' : 'Lightweight: show stills instead of decoding video'" wrapper-class="inline-flex">
				<FormButton type="light" is-icon size="sm" :class="lightweight ? '!text-gold bg-surface-3' : ''" :aria-pressed="lightweight" aria-label="Lightweight mode" data-testid="lightweight-toggle" @click="lightweight = !lightweight">
					<icon-base width="13" height="13" :width-view-box="12" :height-view-box="16" fill="none"><icon-lightning /></icon-base>
				</FormButton>
			</Tooltip>
			<Tooltip :content="audioMonitor ? 'Monitoring audio in this browser only' : 'Monitor audio here (does not affect the output)'" wrapper-class="inline-flex">
				<FormButton type="light" is-icon size="sm" :class="toolButton(audioMonitor)" :aria-pressed="audioMonitor" aria-label="Monitor audio" data-testid="audio-monitor-toggle" @click="audioMonitor = !audioMonitor">
					<icon-base width="16" height="16" :width-view-box="20" :height-view-box="20" fill="none">
						<icon-player-volume-on v-if="audioMonitor" />
						<icon-player-volume-off v-else />
					</icon-base>
				</FormButton>
			</Tooltip>
			</template>

			<span v-if="drafting" class="ml-auto flex min-w-0 items-center gap-0.5">
				<Tooltip :content="`${draftLabel}. ${sessionActive ? 'Viewers still see the on-air scene.' : 'Program keeps the saved scene.'}`" wrapper-class="inline-flex min-w-0" popper-class="max-w-[260px] text-sm break-words">
					<span class="mr-1 inline-flex min-w-0 items-center gap-1.5 text-xs font-medium text-gold" role="status" data-testid="draft-bar">
						<span class="size-2 shrink-0 rounded-full bg-gold" aria-hidden="true" />
						<span class="mf-strip-draft-label truncate">{{ draftShortLabel }}</span>
						<span class="sr-only">{{ draftLabel }}</span>
					</span>
				</Tooltip>
				<Tooltip content="Undo (⌘Z)" wrapper-class="inline-flex">
					<FormButton type="light" is-icon size="sm" :disabled="!desk.canUndo.value" aria-label="Undo" data-testid="draft-undo" @click="desk.undo()">
						<icon-base width="15" height="15" fill="none"><icon-undo /></icon-base>
					</FormButton>
				</Tooltip>
				<Tooltip content="Redo (⇧⌘Z)" wrapper-class="inline-flex">
					<FormButton type="light" is-icon size="sm" :disabled="!desk.canRedo.value" aria-label="Redo" data-testid="draft-redo" @click="desk.redo()">
						<icon-base width="15" height="15" fill="none"><icon-redo /></icon-base>
					</FormButton>
				</Tooltip>
				<FormButton v-if="!liveEdit" type="link" size="sm" class="mx-1.5 text-xs" :disabled="desk.applying.value" data-testid="draft-discard" @click="desk.discardDraft()">Discard</FormButton>
				<Tooltip :content="`${applyLabel} (⌘Enter)`" wrapper-class="inline-flex">
					<FormButton type="main" size="sm" class="!px-2.5 text-xs" :is-loading="desk.applying.value" data-testid="draft-apply" @click="desk.applyDraft()">{{ applyLabel }}</FormButton>
				</Tooltip>
			</span>
			<span v-else class="ml-auto flex shrink-0 items-center gap-0.5">
				<span v-if="liveTarget && liveEdit" class="mr-1 size-2 rounded-full bg-error animate-pulse" aria-hidden="true" />
				<span v-if="liveTarget && liveEdit" class="sr-only" role="status">Changes go live immediately.</span>
				<Tooltip
					v-if="liveTarget && enabled"
					:content="liveEdit ? 'Changes go live when you drop them' : 'Changes stay a draft until you apply them'"
					wrapper-class="inline-flex"
					popper-class="max-w-[240px] text-sm break-words"
				>
					<FormSwitch
						:key="liveEdit ? 'on' : `off-${liveSwitchKey}`"
						size="sm"
						:model-value="liveEdit"
						label="Edit on air"
						label-styles="text-xs whitespace-nowrap mr-1.5"
						:class="liveEdit ? 'text-error-light' : 'text-surface-8'"
						data-testid="live-edit-toggle"
						@update:model-value="onLiveEditToggle"
					/>
				</Tooltip>
				<Tooltip content="Fullscreen (Esc to exit)" wrapper-class="inline-flex">
					<FormButton type="light" is-icon size="sm" aria-label="Fullscreen" data-testid="fullscreen-toggle" @click="toggleFullscreen">
						<icon-base width="15" height="15" :width-view-box="20" :height-view-box="20" fill="none"><icon-player-full-screen /></icon-base>
					</FormButton>
				</Tooltip>
				<Tooltip v-if="!popout" content="Open Preview + controls in their own window" wrapper-class="inline-flex">
					<FormButton type="light" is-icon size="sm" aria-label="Pop out" data-testid="popout-button" @click="emit('popout')">
						<icon-base width="14" height="14"><icon-open-external /></icon-base>
					</FormButton>
				</Tooltip>
			</span>
		</div>

		<!-- Position: over the top of the docks, never over the canvas; no layout shift -->
		<div
			v-if="positionOpen && selectedMedia"
			class="mf-posbar mf-popover absolute inset-x-0 top-full z-[60] mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1.5 rounded-lg bg-surface-2 px-2.5 py-1.5 ring-1 ring-surface-4"
			role="group"
			aria-label="Position"
			data-testid="position-popover"
		>
			<span class="mf-posbar-title flex min-w-0 items-center gap-1.5 text-xs">
				<span class="text-[0.65rem] font-semibold uppercase tracking-wider text-surface-6">Position</span>
				<span class="text-surface-6" aria-hidden="true">·</span>
				<span class="max-w-[9rem] truncate text-surface-text-1">{{ selectedMedia.name }}</span>
			</span>
			<span class="mf-posbar-fields flex items-center gap-2" @keydown="onFieldKeydown" @focusout="onFieldsFocusOut">
				<label v-for="k in ['x', 'y', 'w', 'h']" :key="k" class="flex items-center gap-1 text-xs text-surface-7">
					<span class="w-2.5 text-center font-medium uppercase" aria-hidden="true">{{ k }}</span>
					<input
						:value="fields[k]"
						type="text"
						inputmode="decimal"
						autocomplete="off"
						class="mf-posbar-input h-7 rounded-md bg-surface-1 px-1.5 text-right text-xs tabular-nums text-white ring-1 ring-surface-4 focus:outline-none focus:ring-main disabled:opacity-50"
						:aria-label="{ x: 'X (px)', y: 'Y (px)', w: 'Width (px)', h: 'Height (px)' }[k]"
						:data-key="k"
						:disabled="!editable"
						:data-testid="`position-${k}`"
						@input="onField(k, $event.target.value)"
					>
				</label>
				<span class="text-xs text-surface-7">px</span>
			</span>
			<span class="mf-posbar-actions ml-auto flex items-center gap-1.5">
				<span class="inline-flex rounded-md bg-surface-1 p-0.5 ring-1 ring-surface-4" role="radiogroup" aria-label="Fit to canvas">
					<button
						v-for="m in [{ key: 'fit', label: 'Fit' }, { key: 'fill', label: 'Fill' }]"
						:key="m.key"
						type="button"
						role="radio"
						:aria-checked="fitMode === m.key"
						class="h-6 rounded px-2 text-xs font-medium"
						:class="fitMode === m.key ? 'bg-surface-4 text-white' : 'text-surface-8 hover:text-white'"
						:disabled="!editable"
						:data-testid="`position-${m.key}`"
						@click="setFit(m.key)"
					>{{ m.label }}</button>
				</span>
				<Tooltip content="Reset to fullscreen" wrapper-class="inline-flex">
					<FormButton type="link" size="sm" class="text-xs" :disabled="!editable" data-testid="position-reset" @click="resetFullscreen">Reset</FormButton>
				</Tooltip>
				<button type="button" class="inline-flex size-6 items-center justify-center rounded text-surface-7 hover:bg-surface-3 hover:text-white" aria-label="Close Position" data-testid="position-close" @click="positionOpen = false">✕</button>
			</span>
		</div>
		</div>

		<!-- Hints (draft state lives in the strip, Position below it: nothing here appears on its own) -->
		<div v-if="(!interactive && flow) || suggestLightweight" class="flex min-w-0 flex-col gap-2 lg:col-start-1 lg:row-start-4">
			<p v-if="!interactive && flow" class="m-0 rounded-lg bg-surface-2 px-3 py-2 text-xs text-surface-8 ring-1 ring-surface-3">
				View only. Edit on a larger screen.
			</p>
			<p v-else-if="suggestLightweight" class="m-0 flex flex-wrap items-center gap-2 rounded-lg bg-surface-2 px-3 py-2 text-xs text-surface-8 ring-1 ring-surface-3">
				<span>{{ heavy }} video layers. Preview may get slow.</span>
				<FormButton size="xs" type="secondary" @click="lightweight = true">Use Lightweight</FormButton>
				<FormButton size="xs" type="link" aria-label="Dismiss" @click="dismissedHeavyHint = true">Dismiss</FormButton>
			</p>
		</div>
	</div>
</template>

<style scoped>
.mf-strip {
	container-type: inline-size;
	transition: background-color 150ms ease, box-shadow 150ms ease;
}
.mf-strip--draft {
	background-color: color-mix(in srgb, var(--gold-custom) 9%, var(--surface-2, #1b2133));
	--tw-ring-color: color-mix(in srgb, var(--gold-custom) 45%, transparent);
}
.mf-strip--live {
	background-color: color-mix(in srgb, rgb(222, 94, 94) 9%, var(--surface-2, #1b2133));
	--tw-ring-color: color-mix(in srgb, rgb(222, 94, 94) 50%, transparent);
}
.mf-popover {
	box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);
}
.mf-strip-cell {
	container: mf-cell / inline-size;
}
/* The title takes what is left (the name truncates), so it never forces a wrap. */
.mf-posbar-title {
	flex: 1 1 0;
}
.mf-posbar-input {
	width: 60px;
}
/* Too narrow for one row: title + actions on top, the four numbers below. */
@container mf-cell (max-width: 620px) {
	.mf-posbar-fields { order: 3; width: 100%; justify-content: space-between; }
	.mf-posbar-input { width: 56px; }
}
@container mf-cell (max-width: 360px) {
	.mf-posbar-input { width: 50px; }
}
@container (max-width: 330px) {
	.mf-strip-draft-label { display: none; }
}
</style>
